import React, { useEffect, useState } from 'react';
import { showToast } from '@global/design-system/molecules';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '@app/redux/types';
import {
	StyledGrid,
	StyledColumn,
	LoginLoaderContainer,
} from './LoginScreen.styles';
import { ToastMessage } from '@app/constants';
import { env } from '@app/env';

import { useAuth } from '@app/services/Auth/useAuth';
import authConfig from '@app/services/Auth/authConfig';
import EmailCheck from '../EmailCheck/EmailCheck';
import TokenStorage from '@app/services/Auth/TokenStorage';
import { getSecureRedirectPath } from '@app/services/Auth/redirectUtils';
import { getWorkspaceIdFromEntity } from '@app/services/Auth/useNicheAuth';
import { PageRouteEnum } from '@app/constants/pages';
import { SpinningLoader } from '@nicheinc/campus-ds';
import { safeNavigate } from '@app/utils/safeNavigate';

function Login() {
	const navigate = useNavigate();
	const location = useLocation();
	const { authProvider, selectedAuthProviderName, authProvidersSchemas } =
		useAuth();

	const [email, setEmail] = useState('');

	const { emailCheckResponse, workspaceInfo } = useSelector(
		({ auth, workspace }: RootState) => ({
			emailCheckResponse: auth.custom?.emailCheckResponse,
			workspaceInfo: workspace?.workspaceInfo,
		}),
	);

	// Only send redirect when path includes entity UUID
	const redirectPath = location.pathname.includes('/entity/')
		? `${location.pathname}${location.search}${location.hash}`
		: undefined;

	const performProviderLogin = () => {
		const params = new URLSearchParams(location.search);

		if (location.search) {
			sessionStorage.setItem('preAuthSearchParams', location.search);
		}

		if (selectedAuthProviderName === 'auth0' && authProvidersSchemas?.auth0) {
			authProvidersSchemas.auth0.loginWithRedirect({
				screen_hint: params?.get('login_hint') || 'login', //We're mixing login_hint and screen_hint implementation, in email and others it comes as the login_hint param
				login_hint: email,
				env: env.NODE_ENV,
			});
		}

		if (
			selectedAuthProviderName === 'propelAuth' &&
			authProvidersSchemas?.propelAuth
		) {
			if (emailCheckResponse?.ssoLoginPage) {
				window.location.href =
					emailCheckResponse?.ssoLoginPage + `?login_hint=${email}`;
			} else {
				const encryptedEmail = btoa(email);
				authProvidersSchemas.propelAuth.loginWithRedirect({
					postLoginRedirectUrl: authConfig.redirectUri,
					userSignupQueryParameters: {
						e: encryptedEmail,
					},
				});
			}
		}

		if (selectedAuthProviderName === 'niche' && authProvidersSchemas?.niche) {
			if (emailCheckResponse?.nicheLoginPage) {
				window.location.href = emailCheckResponse?.nicheLoginPage;
			} else {
				authProvidersSchemas.niche.loginWithRedirect();
			}
		}
	};

	useEffect(() => {
		if (location.search.includes('unauthorized=true')) {
			showToast({ message: ToastMessage.errorPermissionDenied, type: 'error' });
		}
	}, []);

	useEffect(() => {
		const locationState = location.state as any;
		const condition = locationState ? locationState.fromOther || false : false;

		if (condition) {
			showToast({ message: ToastMessage.errorLoginFirst, type: 'error' });
			const pathname = location.pathname;
			// Navigate safely with state
			safeNavigate(navigate, pathname, undefined, {
				state: { ...locationState, fromOther: false },
			});
		}
	}, []);

	useEffect(() => {
		const handleAuthenticatedUser = async () => {
			const hasIdentityToken = !!TokenStorage.getIdentity();
			if (
				authProvider?.isAuthenticated &&
				selectedAuthProviderName &&
				hasIdentityToken
			) {
				// Check if this is an entity URL that requires workspace switching
				const pathname = location.pathname;
				const entityMatch = pathname.match(/\/entity\/([a-f0-9-]{24,36})/i);

				// If there's an entity UUID, we need to wait for workspace to load first
				if (entityMatch && authProvider?.name === 'niche') {
					if (!workspaceInfo?._id) {
						return; // Wait for workspace to load
					}

					if (!authProvider?.user?.email) {
						return;
					}

					const entityUUID = entityMatch[1];

					// Check if entity corresponds to a different workspace
					const newWorkspaceId = await getWorkspaceIdFromEntity(
						entityUUID,
						workspaceInfo._id,
					);

					if (newWorkspaceId) {
						// Need to switch workspace before redirecting
						const params = new URLSearchParams(location.search);
						const redirectParam = params.get('redirect');

						navigate(
							`/${PageRouteEnum.changingWorkspace}/redirect/entity/${entityUUID}`,
							{
								state: {
									workspace: {
										id: newWorkspaceId,
										email: authProvider.user.email,
									},
									redirect: redirectParam,
								},
							},
						);
						return; // Don't proceed with normal redirect
					}

					// Same workspace - proceed with normal redirect below
				}

				// Normal redirect (no workspace switch needed)
				const params = new URLSearchParams(location.search);
				const redirectParam = params.get('redirect');
				const redirectPath = getSecureRedirectPath(
					redirectParam,
					location.hash,
					location.pathname,
				);

				// Navigate safely to redirect path
				safeNavigate(navigate, redirectPath);
			} else {
				performProviderLogin();
			}
		};

		handleAuthenticatedUser();
	}, [
		authProvider?.isAuthenticated,
		selectedAuthProviderName,
		workspaceInfo?._id,
	]);

	useEffect(() => {
		if (!authProvider?.isAuthenticated && selectedAuthProviderName) {
			performProviderLogin();
		}
	}, [authProvider?.isAuthenticated, selectedAuthProviderName]);

	return (
		<div>
			<div>
				{!selectedAuthProviderName ? (
					<EmailCheck
						email={email}
						setEmail={setEmail}
						redirectPath={redirectPath}
					/>
				) : (
					<>
						{!authProvider?.isLoading &&
							!authProvider?.isAuthenticated &&
							selectedAuthProviderName && (
								<StyledGrid>
									<StyledColumn>
										<LoginLoaderContainer>
											<SpinningLoader />
										</LoginLoaderContainer>
									</StyledColumn>
								</StyledGrid>
							)}
					</>
				)}
			</div>
		</div>
	);
}

export default Login;
